import React, { useCallback, useRef } from 'react';
import { Box, Checkbox } from '@mui/material';

interface EditableCheckboxInputProps {
  value: any;
  onChange: (value: any) => void;
  onSave: (value: any) => void;
  onCancel: () => void;
}

const WRAPPER_SX = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  width: '100%',
  minHeight: '24px',
} as const;

const CHECKBOX_SX = { padding: 0, margin: 0 } as const;

export const EditableCheckboxInput: React.FC<EditableCheckboxInputProps> = ({
  value,
  onChange,
  onSave,
  onCancel,
}) => {
  const isChecked = value === true;
  const hasCommittedRef = useRef(false);

  const commit = useCallback(
    (newValue: boolean) => {
      if (hasCommittedRef.current) return;
      hasCommittedRef.current = true;
      onChange(newValue);
      onSave(newValue);
    },
    [onChange, onSave],
  );

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      commit(event.target.checked);
    },
    [commit],
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onCancel();
      } else if (event.key === 'Enter') {
        event.preventDefault();
        commit(isChecked);
      }
    },
    [commit, isChecked, onCancel],
  );

  const handleBlur = useCallback(() => {
    if (!hasCommittedRef.current) onCancel();
  }, [onCancel]);

  return (
    <Box sx={WRAPPER_SX}>
      <Checkbox
        checked={isChecked}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        color='primary'
        size='small'
        autoFocus
        sx={CHECKBOX_SX}
      />
    </Box>
  );
};
